import {
  useLegalClient,
  useLegalClientLegalHead,
  useLegalClientLegalHeadSnapshot,
} from 'api/requests';
import { LegalClientVersionEntity } from 'api/types/entity';
import { NavigationBreadcrumbsTitles } from 'components';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate, usePageTitle, RoutesId } from 'libs/navigation';

import { getLegalClientLegalHeadInitialValue } from '../helpers';

import { useLegalClientLegalHeadVersions } from './useLegalClientLegalHeadVersions';

type ContainerParams = {
  legalClientId: string;
  headId: string;
  versionId?: string;
};

export const useLegalClientLegalHeadVersion = () => {
  const { t } = useTranslation();

  const { params, navigate } = useNavigate<ContainerParams>();

  const { loading: loadingLegalHead, legalClientLegalHead } =
    useLegalClientLegalHead(params.headId!, true);

  const { legalClient } = useLegalClient(params.legalClientId!);

  const { loading: versionsLoading, versions } =
    useLegalClientLegalHeadVersions(
      params.legalClientId!,
      params.headId!,
      params.versionId!
    );

  const {
    snapshot,
    loading: snapshotLoading,
    error,
    isError,
  } = useLegalClientLegalHeadSnapshot(params.headId!, params.versionId!, {
    fetchPolicy: 'no-cache',
  });

  const initialValues = snapshot?.value
    ? getLegalClientLegalHeadInitialValue(snapshot.value, t)
    : undefined;

  const fullName = legalClientLegalHead?.fullName || '';

  const clientFullName = legalClient?.fullName;

  const breadcrumbsTitles: NavigationBreadcrumbsTitles = {
    [RoutesId.legalClient]: clientFullName,
    [RoutesId.legalClientVersion]: t('client.client.versions.header', {
      name: clientFullName,
    }),
    [RoutesId.legalClientLegalHeadVersion]: t('client.client.versions.header', {
      name: fullName,
    }),
  };

  const loading =
    (!legalClientLegalHead && loadingLegalHead) ||
    (!versions && versionsLoading);

  const onCloseVersionHistory = () => {
    if (legalClientLegalHead?.deletedAt) {
      navigate(ROUTES.clients.legalClients.legalClient.fullPath, {
        legalClientId: params.legalClientId!,
      });
    } else {
      navigate(
        ROUTES.clients.legalClients.legalClient.legalClientLegalHead.fullPath,
        {
          legalClientId: params.legalClientId!,
          headId: params.headId!,
        }
      );
    }
  };

  const onChangeVersion = (version: LegalClientVersionEntity) => {
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientLegalHeadVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        headId: params.headId!,
        versionId: version.id,
      }
    );
  };

  const onClickFirstHead = (firstHeadId: string) => {
    navigate(
      ROUTES.clients.legalClients.legalClient.legalClientVersion
        .legalClientLegalHeadVersion.legalClientFirstHeadVersion.fullPath,
      {
        legalClientId: params.legalClientId!,
        headId: params.headId!,
        firstHeadId,
        versionId: params.versionId!,
      }
    );
  };

  usePageTitle(fullName);

  return {
    versionId: params.versionId!,
    snapshot,
    loading,
    snapshotLoading: !snapshot && snapshotLoading,
    error,
    versions,
    fullName,
    initialValues,
    breadcrumbsTitles,
    isError,
    onChangeVersion,
    onCloseVersionHistory,
    onClickFirstHead,
  };
};
